import React, { useContext } from 'react';
import styled from 'styled-components';
import Clipboard from 'react-clipboard.js';

import i18n from '../i18n';
import { showSuccess } from '../services/toastService';
import Icon from './Icon';
import ThemeContext from '../contexts/ThemeContext';

// min-width: 0 lets the flex container shrink below its content. Flex items
// otherwise refuse to go under their min-content width, and an unbroken digit
// string has a very wide min-content, so the cell used to overflow past the
// td's max-width into the neighbouring column.
const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// Digits an ordinary order number is expected to keep on one line.
const ORDER_NUMBER_DIGITS = 8;

// Order numbers can be long unbroken digit strings ('216485545413448451') with
// no natural break opportunity, so they wrap at any character rather than
// overflow into the neighbouring column (TUL-5698). Declaration by declaration:
//
// - min-width: a ch floor, not a pixel width. Breaking anywhere drives
//   min-content down to a single digit, so without it a table that sizes this
//   column to its content could wrap even a short order number. The value is a
//   flex item, hence blockified, so min-width applies.
// - white-space: the surrounding cell may set nowrap for its short columns,
//   which would inherit down here and defeat the wrapping below.
// - word-break: fallback for browsers without overflow-wrap: anywhere. The
//   content is always an identifier, so breaking mid-"word" is harmless.
const Value = styled.span`
  min-width: ${ORDER_NUMBER_DIGITS}ch;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-all;
`;

// flex-shrink: 0 keeps the icon at full size while the number beside it wraps.
const CopyButton = styled(Clipboard)`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.7;
  flex-shrink: 0;
  &:hover {
    opacity: 1;
  }
`;

const OrderNumberCell = ({ value }) => {
  const { colors } = useContext(ThemeContext);

  if (!value) {
    return null;
  }

  return (
    <Container>
      <Value>{value}</Value>
      <CopyButton
        data-clipboard-text={value}
        button-title={i18n('copy_reservation')}
        onSuccess={() => showSuccess(i18n('copied_to_clipboard'))}
      >
        <Icon
          icon="content_copy"
          iconLib="material"
          iconColor={colors.webDarkBlue}
          iconSize={16}
          withoutContainer
        />
      </CopyButton>
    </Container>
  );
};

export default OrderNumberCell;
